import React, { useState, useEffect } from 'react';
import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent'; // Make sure you import CardContent correctly
import CardActions from '@mui/material/CardActions';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';
import skip from './assets/skip.png';
import forgot from './assets/forgot.png';
import partially from './assets/partially.png';
import efforts from './assets/efforts.png';
import convenient from './assets/convenient.png';
import { useNavigate, useParams } from 'react-router-dom';
import katex from 'katex'; // Import KaTeX for LaTeX rendering
import sanitizeHtml from 'sanitize-html'; // Import sanitize-html for sanitizing HTML content
import 'katex/dist/katex.min.css'; // KaTeX styles
import { useContext } from "react";
import { ThemeContext } from '../context/ThemeContext';
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogActions from "@mui/material/DialogActions";
import { DialogContent, CircularProgress } from '@mui/material'; 




const FlipCard = styled(Card)(({ theme }) => ({
  width: '80%',
  height: '80%',
  perspective: 1000,
  position: 'relative',
  backgroundColor: 'white',
  borderRadius: '20px',
}));

const FlipCardInner = styled('div')(({ theme }) => ({
  position: 'absolute',
  width: '100%',
  height: '100%',
  transition: 'transform 0.8s',
  transformStyle: 'preserve-3d',
}));

const FlipCardFront = styled(CardContent)(({ theme }) => ({
  position: 'absolute',
  width: '100%',
  height: '100%',
  backfaceVisibility: 'hidden',
  backgroundColor: 'white',
  color: 'black',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: theme.spacing(2),
}));

const StyledButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#1627af',
  color: 'white',
  width: '100%',
  height: '60px',
  fontSize: '1.25rem',
  '&:hover': {
    backgroundColor: '#44a6e9',
  },
}));

const ImageButton = styled(IconButton)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
}));

const Image = styled('img')(({ theme }) => ({
  width: '60px',
  height: '60px',
}));

// Utility function to strip HTML tags
const stripHtmlTags = (text) => {
  if (!text) return '';
  return text.replace(/<[^>]*>/g, '');
};

// Function to render LaTeX and HTML content
const renderContent = (html) => {
  if (!html) return null;

  // Split the content by LaTeX syntax
  const parts = html.split(/(\$\$.+?\$\$|\$.+?\$)/g); // Split by LaTeX syntax

  return parts.map((part, index) => {
    if (part.startsWith('$$') && part.endsWith('$$')) {
      // Block-level KaTeX
      const latex = part.slice(2, -2); // Remove the $$ delimiters
      try {
        const renderedLatex = katex.renderToString(latex, { displayMode: true });
        return (
          <div
            key={index}
            dangerouslySetInnerHTML={{ __html: renderedLatex }}
            className="rendered-content"
          />
        );
      } catch (err) {
        console.error('KaTeX rendering failed:', err);
        return <span key={index}>{part}</span>;
      }
    } else if (part.startsWith('$') && part.endsWith('$')) {
      // Inline KaTeX
      const latex = part.slice(1, -1); // Remove the $ delimiters
      try {
        const renderedLatex = katex.renderToString(latex);
        return (
          <span
            key={index}
            dangerouslySetInnerHTML={{ __html: renderedLatex }}
            className="rendered-content"
          />
        );
      } catch (err) {
        console.error('KaTeX rendering failed:', err);
        return <span key={index}>{part}</span>;
      }
    } else {
      // Sanitize and render HTML content
      const cleanHtml = sanitizeHtml(part, {
        allowedTags: sanitizeHtml.defaults.allowedTags.concat(['img', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6']),
        allowedAttributes: {
          ...sanitizeHtml.defaults.allowedAttributes,
          img: ['src', 'alt'],
          a: ['href', 'target'],
        },
      });
      return (
        <span
          key={index}
          dangerouslySetInnerHTML={{ __html: cleanHtml }}
          className="rendered-content"
        />
      );
    }
  });
};

export default function McqCard() {
  const [flashcards, setFlashcards] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showAnswer, setShowAnswer] = useState(false);
  const { subfolder_id } = useParams();
  const { text } = useParams();
  const navigate = useNavigate();
  // const { darkMode } = useContext(ThemeContext);
  const [openDialog, setOpenDialog] = useState(false);
    const [loading, setLoading] = useState(true); 
  const { mode, customColors } = useContext(ThemeContext);
  const apiUrl = process.env.REACT_APP_BASE_URL;

  useEffect(() => {
    const fetchFlashcards = async () => {
      try {
        const token = sessionStorage.getItem("authToken");
        if (!token) {
          console.error("No authentication token found");
          return;
        }

        const response = await fetch(`${apiUrl}/subfolder/${subfolder_id}/mcq/`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const data = await response.json();
        // Keep the full answers array instead of extracting only the correct answer
        setFlashcards(data);
          if (!data || data.length === 0) {
        setOpenDialog(true);
      }
      } catch (error) {
        console.error("Error fetching data:", error);
        setOpenDialog(true);
      } finally {
        setLoading(false); // ✅ stop loading when API done
      }
    };

    fetchFlashcards();
  }, [subfolder_id], text);

  const handleFlip = () => {
    setShowAnswer(!showAnswer);
  };

  const handleNext = async (feedback) => {
    if (currentIndex < flashcards.length - 1) {
      setCurrentIndex(currentIndex + 1);
    } else {
      setCurrentIndex(0);
    }
    setShowAnswer(false);

    try {
      const token = sessionStorage.getItem("authToken"); // Retrieve the token from localStorage
      if (!token) {
        console.error("No authentication token found");
        return;
      }

      const questionId = flashcards[currentIndex].id;
      const subfolder_id = flashcards[currentIndex].subfolder_id; // Ensure you have the correct subfolder_i
      const question_type = flashcards[currentIndex].question_type;
      const apiUrl = process.env.REACT_APP_BASE_URL;

      const response = await fetch(
        `${apiUrl}/practice-logs/`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`, // Include the Bearer token in the Authorization header
          },
          body: JSON.stringify({
            flashcard_type: question_type,
            flashcard_id: questionId,
            feedback: feedback,
            meta: { subfolder_id: subfolder_id }
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      console.log("Feedback sent successfully.");
    } catch (error) {
      console.error("Error sending feedback:", error);
    }
  };


  const handleClose = () => {
    navigate(-1); // Navigate back to the previous page in the history
  };
   const handleCloseDialog = () => {
    setOpenDialog(false);
    navigate(-1);
  };

  // 🌀 Show loading spinner until API finishes
  if (loading) {
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100vh",
      }}
    >
      <CircularProgress />
    </Box>
  );
}


if (!loading && (!flashcards || flashcards.length === 0)) {
  return (
    <Dialog open={openDialog} onClose={handleCloseDialog}>
      <DialogContent
        sx={{
          backgroundColor:
            mode === "dark"
              ? "#333"
              : mode === "custom"
              ? customColors.bg
              : "white",
        }}
      >
        <DialogTitle
          sx={{
            color:
              mode === "dark"
                ? "white"
                : mode === "custom"
                ? customColors.text
                : "black",
          }}
        >
          No questions available
        </DialogTitle>
        <DialogActions>
          <Button onClick={handleCloseDialog} variant="outlined" color="primary">
            Close
          </Button>
        </DialogActions>
      </DialogContent>
    </Dialog>
  );
}

  return (
    <Box
      className={`practice-modal ${mode}-mode`}
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100vh",
        background: "linear-gradient(to right, #142be1, #44a6e9)",
      }}
    >
      <FlipCard>
        <FlipCardInner>
          <FlipCardFront className={`practice-card ${mode}-mode`}>
            <Box
              sx={{
                width: "100%",
                display: "flex",
                justifyContent: "flex-end",
                p: 1,
              }}
            >
              <IconButton onClick={handleClose}>
                <CloseIcon />
              </IconButton>
            </Box>
            <Box className={`practice-card ${mode}-mode`}
              sx={{
                maxHeight: "400px",
                overflowY: "auto",
                padding: 2,
                border: mode === "dark" ? "1px solid #444" : "1px solid #ccc",
                borderRadius: "8px",
                // backgroundColor: "#f9f9f9",
                width: "100%",
              }}
            >
              <Typography
                variant="h4"
                component="div"
                sx={{
                  textAlign: "center",
                  wordWrap: "break-word",
                  padding: 2,
                }}
              >
                <strong>Question:</strong>{" "}
                {renderContent(stripHtmlTags(flashcards[currentIndex].statement))}
              </Typography>
              {/* Display MCQ options in a 2x2 grid when showAnswer is false */}
              {!showAnswer && (
                <Box
                  sx={{
                    mt: 2,
                    display: "flex",
                    flexDirection: "row",
                    flexWrap: "wrap",
                    gap: 2,
                    justifyContent: "center",
                    // Ensure two options per row
                    "& > *": {
                      flex: "1 1 calc(50% - 16px)", // 50% width minus gap
                      maxWidth: "calc(50% - 16px)", // Limit to half the container width
                    },
                  }}
                >
                  {flashcards[currentIndex].answers.map((option, index) => (
                    <Typography
                      key={index}
                      variant="h6" // Increased font size
                      component="div"
                      sx={{
                        textAlign: "center",
                        wordWrap: "break-word",
                        padding: 1,
                        border: "1px solid #eee",
                        borderRadius: "4px",
                        minWidth: "150px",
                        fontSize: "1.25rem", // Explicitly increase font size
                      }}
                    >
                      {`${String.fromCharCode(65 + index)}. ${option.answer_text}`}
                    </Typography>
                  ))}
                </Box>
              )}
              {showAnswer && (
                <Typography
                  variant="h4"
                  component="div"
                  sx={{
                    textAlign: "center",
                    mt: 2,
                    wordWrap: "break-word",
                    padding: 2,
                  }}
                >
                  <strong>Answer:</strong>{" "}
                  {renderContent(
                    stripHtmlTags(
                      flashcards[currentIndex].answers.find((answer) => answer.is_correct)
                        ?.answer_text || ""
                    )
                  )}
                </Typography>
              )}
              {showAnswer && flashcards[currentIndex].explanation && (
                <Typography
                  variant="h5"
                  component="div"
                  sx={{
                    textAlign: "center",
                    mt: 2,
                    wordWrap: "break-word",
                    padding: 2,
                  }}
                >
                  <strong>Explanation:</strong>{" "}
                  {renderContent(stripHtmlTags(flashcards[currentIndex].explanation))}
                </Typography>
              )}
            </Box>
            <CardActions sx={{ width: "100%" }}>
              {!showAnswer && (
                <StyledButton
                  variant="contained"
                  onClick={handleFlip}
                  startIcon={<VisibilityIcon />}
                >
                  Show Answer
                </StyledButton>
              )}
            </CardActions>
            {showAnswer && (
              <Box sx={{ width: "100%", mt: 2 }}>
                <Box sx={{ display: "flex", justifyContent: "space-around" }}>
                  <ImageButton onClick={() => handleNext("skip")}>
                    <Image src={skip} alt="skip" />
                    <Typography variant="caption">Skip</Typography>
                  </ImageButton>
                  <ImageButton onClick={() => handleNext("forgot")}>
                    <Image src={forgot} alt="forgot" />
                    <Typography variant="caption">Forgot</Typography>
                  </ImageButton>
                  <ImageButton onClick={() => handleNext("partially recalled")}>
                    <Image src={partially} alt="partially" />
                    <Typography variant="caption">Partially Recalled</Typography>
                  </ImageButton>
                  <ImageButton onClick={() => handleNext("recalled with effort")}>
                    <Image src={efforts} alt="efforts" />
                    <Typography variant="caption">Recalled with Efforts</Typography>
                  </ImageButton>
                  <ImageButton onClick={() => handleNext("easily recalled")}>
                    <Image src={convenient} alt="convenient" />
                    <Typography variant="caption">Easily Recalled</Typography>
                  </ImageButton>
                </Box>
              </Box>
            )}
          </FlipCardFront>
        </FlipCardInner>
      </FlipCard>
    </Box>
  );
}